import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';
import { cn } from '@/lib/utils';
import { WorklynxMark } from '@/components/brand/WorklynxBrand';
import { useFeatureEntitlement } from '@/hooks/useFeatureEntitlements';
import { useEffectivePermissions } from '@/hooks/usePermissions';
import { useRole } from '@/hooks/useRole';
import { useOrg } from '@/hooks/useOrg';
import { APP_NAME } from '@/lib/constants';
import { SHIFT_CSV_IMPORTS_FEATURE } from '@/lib/features';
import type { PermissionKey } from '@/lib/permissions';
import type { Role } from '@/types/roles';
import {
  getSectionNavigationItems,
  type NavigationSection,
} from '@/lib/sectionNavigation';

/**
 * Nav icons are 20px, as they were when they were Tailwind's h-5 w-5. The WorkLynx set
 * takes a number instead of a class, because the inline variant scales its own stroke
 * and a CSS size would leave the glyph drawn for a different one.
 */
const NAV_ICON = 20;

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
  activePaths?: string[];
}

interface NavAccess {
  canUseCsvImports: boolean;
  hasPermission: (permission: PermissionKey) => boolean;
}

const allowAllNavAccess: NavAccess = {
  canUseCsvImports: false,
  hasPermission: () => true,
};

function getNavItems(role: Role | null, access: NavAccess = allowAllNavAccess): NavItem[] {

  const buildSectionNavItem = (
    section: NavigationSection,
    label: string,
    icon: ReactNode,
  ): NavItem | null => {
    const items = getSectionNavigationItems(section, role, access);
    if (!items.length) return null;

    return {
      label,
      path: items[0].path,
      icon,
      activePaths: items.flatMap((item) => [item.path, ...(item.relatedPaths ?? [])]),
    };
  };

  switch (role) {
    case 'EMPLOYEE':
      return [
        {
          label: 'Dashboard',
          path: '/employee/dashboard',
          icon: <WorklynxIcon name="dashboard" size={NAV_ICON} />,
        },
        // Self-service a manager has switched off leaves the menu with it.
        ...(access.hasPermission('shifts.own.view')
          ? [
              {
                label: 'My Shifts',
                path: '/employee/my-shifts',
                icon: <WorklynxIcon name="roster" size={NAV_ICON} />,
              },
              {
                label: 'Available Shifts',
                path: '/employee/available-shifts',
                icon: <WorklynxIcon name="calendar-add" size={NAV_ICON} />,
              },
            ]
          : []),
        ...(access.hasPermission('timesheets.own.view')
          ? [{ label: 'Timesheets', path: '/employee/timesheets', icon: <WorklynxIcon name="timesheet" size={NAV_ICON} /> }]
          : []),
        ...(access.hasPermission('leave.own.view')
          ? [{ label: 'Leave', path: '/employee/leave', icon: <WorklynxIcon name="leave" size={NAV_ICON} /> }]
          : []),
        {
          label: 'Availability',
          path: '/employee/availability',
          icon: <WorklynxIcon name="checklist" size={NAV_ICON} />,
        },
        {
          label: 'Messages',
          path: '/employee/communication',
          icon: <WorklynxIcon name="messages" size={NAV_ICON} />,
        },
        { label: 'Payslips', path: '/employee/payslips', icon: <WorklynxIcon name="payslip" size={NAV_ICON} /> },
        { label: 'Invoices', path: '/employee/invoices', icon: <WorklynxIcon name="invoice" size={NAV_ICON} /> },
      ];
    case 'MANAGER':
      return [
        {
          label: 'Dashboard',
          path: '/manager/dashboard',
          icon: <WorklynxIcon name="dashboard" size={NAV_ICON} />,
        },
        ...([buildSectionNavItem('shifts', 'Shifts', <WorklynxIcon name="roster" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        ...([
          buildSectionNavItem('approvals', 'Approvals', <WorklynxIcon name="approve" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        ...([buildSectionNavItem('team', 'Team', <WorklynxIcon name="team" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        {
          label: 'Communication',
          path: '/manager/communication',
          icon: <WorklynxIcon name="messages" size={NAV_ICON} />,
        },
        ...(access.hasPermission('audit_log.view')
          ? [
              {
                label: 'Audit Log',
                path: '/manager/audit-log',
                icon: <WorklynxIcon name="audit" size={NAV_ICON} />,
              },
            ]
          : []),
      ];
    case 'BM': {
      return [
        {
          label: 'Dashboard',
          path: '/bm/dashboard',
          icon: <WorklynxIcon name="dashboard" size={NAV_ICON} />,
        },
        ...([buildSectionNavItem('shifts', 'Shifts', <WorklynxIcon name="roster" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        ...([
          buildSectionNavItem('approvals', 'Approvals', <WorklynxIcon name="approve" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        ...([buildSectionNavItem('team', 'Team', <WorklynxIcon name="team" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        ...(access.hasPermission('reports.view')
          ? [{ label: 'Reports', path: '/bm/reports', icon: <WorklynxIcon name="reports" size={NAV_ICON} /> }]
          : []),
        ...([
          buildSectionNavItem('settings', 'Settings', <WorklynxIcon name="settings" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        {
          label: 'Communication',
          path: '/bm/communication',
          icon: <WorklynxIcon name="messages" size={NAV_ICON} />,
        },
        ...(access.hasPermission('invoices.view')
          ? [{ label: 'Invoices', path: '/bm/invoices', icon: <WorklynxIcon name="invoice" size={NAV_ICON} /> }]
          : []),
        ...(access.hasPermission('audit_log.view')
          ? [
              {
                label: 'Audit Log',
                path: '/bm/audit-log',
                icon: <WorklynxIcon name="audit" size={NAV_ICON} />,
              },
            ]
          : []),
      ];
    }
    case 'BO': {
      return [
        {
          label: 'Dashboard',
          path: '/bo/dashboard',
          icon: <WorklynxIcon name="dashboard" size={NAV_ICON} />,
        },
        ...([buildSectionNavItem('shifts', 'Shifts', <WorklynxIcon name="roster" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        ...([
          buildSectionNavItem('clients', 'Clients', <WorklynxIcon name="client" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        ...([buildSectionNavItem('team', 'Team', <WorklynxIcon name="team" size={NAV_ICON} />)].filter(
          Boolean,
        ) as NavItem[]),
        ...([
          buildSectionNavItem('approvals', 'Approvals', <WorklynxIcon name="approve" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        { label: 'Reports', path: '/bo/reports', icon: <WorklynxIcon name="reports" size={NAV_ICON} /> },
        ...([
          buildSectionNavItem('settings', 'Settings', <WorklynxIcon name="settings" size={NAV_ICON} />),
        ].filter(Boolean) as NavItem[]),
        {
          label: 'Communication',
          path: '/bo/communication',
          icon: <WorklynxIcon name="messages" size={NAV_ICON} />,
        },
        { label: 'Audit Log', path: '/bo/audit-log', icon: <WorklynxIcon name="audit" size={NAV_ICON} /> },
      ];
    }
    case 'ADMIN':
      return [
        {
          label: 'Dashboard',
          path: '/admin/dashboard',
          icon: <WorklynxIcon name="dashboard" size={NAV_ICON} />,
        },
        { label: 'Organisations', path: '/admin/orgs', icon: <WorklynxIcon name="client" size={NAV_ICON} /> },
        {
          label: 'Billing Plans',
          path: '/admin/billing-plans',
          icon: <WorklynxIcon name="billing" size={NAV_ICON} />,
        },
        {
          label: 'Discount Codes',
          path: '/admin/discount-codes',
          icon: <WorklynxIcon name="tag" size={NAV_ICON} />,
        },
        {
          label: 'Private Beta',
          path: '/admin/private-beta',
          icon: <WorklynxIcon name="rocket" size={NAV_ICON} />,
        },
        {
          label: 'ABN Lookup',
          path: '/admin/abn-lookup',
          icon: <WorklynxIcon name="shield-alert" size={NAV_ICON} />,
        },
      ];
    default:
      // No organisation: either they have left one, or they have not joined yet. Their own
      // record is the whole of what they have, and without this they can reach Profile and
      // then have no way back to it.
      return [
        { label: 'My record', path: '/my-record', icon: <WorklynxIcon name="contact-card" size={NAV_ICON} /> },
      ];
  }
}

function NavItemLink({
  item,
  collapsed,
  darkShell,
  onMobileClose,
}: {
  item: NavItem;
  collapsed: boolean;
  darkShell: boolean;
  onMobileClose?: () => void;
}) {
  const location = useLocation();
  const hasRelatedActivePath = item.activePaths?.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  return (
    <NavLink
      to={item.path}
      onClick={onMobileClose}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
          isActive || hasRelatedActivePath
            ? darkShell
              ? 'bg-brand text-white shadow-sm'
              : 'bg-brand text-white shadow-sm'
            : darkShell
              ? 'hover:bg-brand/15 text-slate-300 hover:text-white'
              : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950',
          collapsed && 'justify-center px-2',
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon}
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const { role } = useRole();
  const { organization } = useOrg();
  const csvImportAccess = useFeatureEntitlement(SHIFT_CSV_IMPORTS_FEATURE);
  const permissions = useEffectivePermissions();
  const navItems = getNavItems(role, {
    canUseCsvImports: csvImportAccess.data?.isEnabled ?? false,
    hasPermission: (permission) => !permissions.isError && permissions.hasPermission(permission),
  });
  const darkShell = true;
  const workspaceName = organization?.name ?? APP_NAME;

  return (
    <aside
      className={cn(
        'flex h-full flex-col transition-all duration-300',
        // The shell is dark whatever the page theme says, so the icons inside it need the
        // lifted accent either way.
        darkShell && 'wl-on-dark',
        darkShell
          ? 'border-r border-slate-900 bg-[#111821] text-white shadow-[6px_0_24px_rgba(15,23,42,0.14)]'
          : 'border-r border-slate-200 bg-white text-slate-950 shadow-[1px_0_0_rgba(15,23,42,0.02)]',
        collapsed ? 'w-16' : 'w-72',
      )}
    >
      {/* Logo / Brand */}
      <div
        className={cn(
          'flex h-[5rem] items-center justify-between border-b px-4',
          darkShell ? 'border-white/10' : 'border-slate-200',
        )}
      >
        {!collapsed && (
          <div className="flex min-w-0 items-center gap-3">
            {organization?.company_logo_url ? (
              <img
                src={organization.company_logo_url}
                alt={organization.name}
                className={cn(
                  'h-10 w-10 rounded-xl object-contain',
                  darkShell ? 'bg-white p-1' : 'bg-slate-50',
                )}
              />
            ) : (
              <span
                className={cn(
                  'flex h-11 w-11 items-center justify-center rounded-xl',
                  darkShell ? 'bg-brand text-white' : 'bg-brand-soft',
                )}
              >
                <WorklynxMark alt="" className="h-8 w-8" />
              </span>
            )}
            <span className="min-w-0">
              <span
                className={cn(
                  'block truncate text-base font-bold',
                  darkShell ? 'text-white' : 'text-slate-950',
                )}
              >
                Worklynx
              </span>
              <span
                className={cn(
                  'block truncate text-xs',
                  darkShell ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                {workspaceName}
              </span>
            </span>
          </div>
        )}
        <button
          onClick={onToggle}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-xl transition-colors',
            darkShell
              ? 'hover:bg-brand/15 text-slate-400 hover:text-white'
              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-950',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <WorklynxIcon name="chevron-right" size={16} /> : <WorklynxIcon name="chevron-left" size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1.5">
          {navItems.map((item) => (
            <li key={item.path + item.label}>
              <NavItemLink
                item={item}
                collapsed={collapsed}
                darkShell={darkShell}
                onMobileClose={onMobileClose}
              />
            </li>
          ))}
        </ul>
      </nav>

      {/* Profile link at bottom */}
      <div className={cn('border-t p-3', darkShell ? 'border-white/10' : 'border-slate-200')}>
        <NavLink
          to="/profile"
          onClick={onMobileClose}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
              isActive
                ? 'bg-brand text-white shadow-sm'
                : darkShell
                  ? 'hover:bg-brand/15 text-slate-300 hover:text-white'
                  : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950',
              collapsed && 'justify-center px-2',
            )
          }
        >
          <WorklynxIcon name="settings" size={NAV_ICON} />
          {!collapsed && <span>Profile</span>}
        </NavLink>
      </div>
    </aside>
  );
}
